import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiCard,
  MiContentDrawer,
  MiDetailFields,
  MiMap,
  MiMarker,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  regionCodesUS,
  specialCharacterRegex,
} from "@miplatform/constants";
import { useEdit, useMapForm } from "@miplatform/hooks";
import { AccountType } from "@miplatform/types";
import { useCityViewModel } from "@miplatform/view-models";
import { mdiClose, mdiHomeGroup, mdiLocationEnter } from "@mdi/js";
import { ServerAutocomplete } from "@mibuildweb/components";
import { useAccounts } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { CityAutocomplete } from "../../../components/AddHomeForm/CityAutocomplete/CityAutocomplete";
import { useCreateCommunityDrawerViewModel } from "./useCreateCommunityDrawerViewModel";

const initialValues = {
  builderId: "",
  name: "",
  cityId: "",
  regionCode: "",
  county: "",
  latitude: 39.8283,
  longitude: -98.5795,
};

const validationRules = [
  {
    name: "builderId",
    validationRule: z.string().min(1, "Builder is required"),
  },
  {
    name: "name",
    validationRule: z
      .string()
      .min(1, "Community name is required")
      .regex(specialCharacterRegex, {
        message: "Community name cannot contain special characters",
      }),
  },
  {
    name: "cityId",
    validationRule: z.string().min(1, "City is required"),
  },
  {
    name: "regionCode",
    validationRule: z.string().min(1, "State is required"),
  },
  {
    name: "latitude",
    validationRule: z
      .number()
      .or(
        z
          .string()
          .regex(/^-?\d+(\.\d+)?$/, "Invalid Latitude")
          .transform(Number)
      )
      .refine((val) => val >= -90 && val <= 90, {
        message: "Latitude must be between -90 and 90 degrees",
      }),
  },
  {
    name: "longitude",
    validationRule: z
      .number()
      .or(
        z
          .string()
          .regex(/^-?\d+(\.\d+)?$/, "Invalid Longitude")
          .transform(Number)
      )
      .refine((val) => val >= -180 && val <= 180, {
        message: "Longitude must be between -180 and 180 degrees",
      }),
  },
];

/**
 * @typedef {Object} CreateCommunityDrawerProps
 * @property {boolean} open - Determines if the drawer is open.
 * @property {Function} onClose - Function to close the drawer.
 * @returns {JSX.Element} The CreateCommunityDrawer component.
 */
export function CreateCommunityDrawer({ open, onClose }) {
  const { classes } = useStyles();
  const { isLoading, createCommunity } = useCreateCommunityDrawerViewModel();
  const { allCities, cities, addCityToState, getCities } = useCityViewModel();
  const { accounts, getAccounts } = useAccounts({
    accountTypes: [AccountType.Builder],
  });

  const edit = useEdit(initialValues, validationRules);
  const lat = edit.getValue("latitude");
  const lng = edit.getValue("longitude");

  const { handleCoordChange } = useMapForm({
    values: edit.getValue(),
    update: edit.update,
    cities,
    addCity: addCityToState,
    getCities,
    isEditing: true,
  });

  const communityFields = [
    {
      label: "Builder",
      name: "builderId",
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      style: { width: "100%" },
      customComponent: (
        <ServerAutocomplete
          value={edit.edits.builderId}
          labelKey="name"
          filterKeys={["name"]}
          options={accounts}
          getter={getAccounts}
          onChange={(builder) => {
            edit.update({
              builderId: builder || "",
            });
          }}
        />
      ),
    },
    {
      label: "Community Name",
      name: "name",
      required: true,
      style: { flex: 1 },
    },
    {
      name: "cityId",
      label: "City",
      style: { flex: 1 },
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <CityAutocomplete
          value={edit.edits.cityId}
          cities={allCities}
          getCities={getCities}
          onChange={(city) => {
            edit.update({
              cityId: city.id,
              city: city.cityName,
              county: city.countyName,
              regionCode: city.regionCode,
            });
          }}
          onCreate={getCities}
        />
      ),
    },
    {
      label: "State",
      name: "regionCode",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      style: { flex: 1 },
    },
    {
      label: "Latitude",
      name: "latitude",
      type: "number",
      width: "100%",
      style: { display: "none" },
    },
    {
      label: "Longitude",
      name: "longitude",
      type: "number",
      width: "100%",
      style: { display: "none" },
    },
    {
      label: "county",
      name: "county",
      style: { display: "none" },
    },
  ];

  const handleSubmit = () => {
    const { latitude, longitude, ...formData } = edit.getValue();
    formData.location = {
      latitude,
      longitude,
    };

    createCommunity(formData);
  };

  const handleCancel = () => {
    edit.reset();
    onClose();
  };

  useEffect(() => {
    getCities();
  }, []);

  return (
    <MiContentDrawer
      title="Create New Community"
      variant="temporary"
      open={open}
      onClose={handleCancel}
      leftIcon={mdiHomeGroup}
      rightIcon={mdiClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleCancel,
        },
        {
          name: "Create Community",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          disabled: !edit.isValid() || isLoading,
          onClick: handleSubmit,
        },
      ]}
    >
      <Box>
        <Box className={classes.contentContainer}>
          <MiCard title="Community Details" leftIcon={mdiHomeGroup}>
            <MiDetailFields
              detailFields={communityFields}
              edit={edit}
              customWrapperStyle={[
                classes.formContainer,
                classes.container,
              ].join(" ")}
            />
          </MiCard>
          <MiCard title="Community Location" leftIcon={mdiLocationEnter}>
            <MiMap
              onCoordChange={handleCoordChange}
              containerProps={{ className: classes.mapContainer }}
            >
              <MiMarker position={{ lat, lng }} />
            </MiMap>
            <Box className={classes.markerContainer}>
              <Box>
                <Typography color="gray" fontSize={14}>
                  Latitude
                </Typography>
                <Typography>{lat}</Typography>
              </Box>
              <Box>
                <Typography color="gray" fontSize={14}>
                  Longitude
                </Typography>
                <Typography>{lng}</Typography>
              </Box>
            </Box>
          </MiCard>
        </Box>
      </Box>
    </MiContentDrawer>
  );
}

const useStyles = makeStyles()(({ palette: { common, grey } }) => ({
  contentContainer: {
    paddingBottom: 8,
  },
  formContainer: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    overflow: "hidden",
  },
  markerContainer: {
    padding: 12,
    paddingTop: 24,
    paddingBottom: 4,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  mapContainer: {
    display: "flex",
    height: 200,
    width: "100%",
    paddingTop: 12,
  },
  container: {
    padding: 12,
    marginTop: 8,
    backgroundColor: common.white,
    boxShadow: `0px 2px 12px 0px ${grey[300]}`,
    borderRadius: "16px",
  },
}));
